import { Modal } from "antd";
import { Button } from "components/atoms/Button";
import { Paragraph } from "components/atoms/Typography";
import { useCreateCustomPlanFromPlan } from "mutations/CustomPlans/useCreateCustomPlanFromPlan";
import posthog from "posthog-js";
import { useState } from "react";
import styles from "./createCustomPlanModal.module.scss";

interface CreateCustomPlanModalProps {
    modalIsOpen: boolean;
    onCancel: () => void;
    setModalIsOpen: (isOpen: boolean) => void;
    carbonReductionPlanUid: string;
    buildingModelUid: string;
    onCreated?: (newBuildingModelCarbonReductionPlanId: string) => void;
}

function CreateCustomPlanModal({
    modalIsOpen,
    onCancel,
    setModalIsOpen,
    carbonReductionPlanUid,
    buildingModelUid,
    onCreated,
}: CreateCustomPlanModalProps) {
    const [planName, setPlanName] = useState("");
    const [errorMessage, setErrorMessage] = useState("");
    const [hasTriedToSubmit, setHasTriedToSubmit] = useState(false);
    const { mutate, isLoading } = useCreateCustomPlanFromPlan();
    const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const planName = e.target.value;
        setPlanName(planName);
        if (hasTriedToSubmit && planName.trim()) {
            setErrorMessage("");
        }
    };

    const modalSubmitHandler = () => {
        setHasTriedToSubmit(true);
        if (!planName.trim()) {
            setErrorMessage("Custom plan name cannot be empty.");
            return;
        }

        posthog.capture("New plan");

        mutate(
            {
                carbonReductionPlanUid,
                planName,
                buildingModelUid,
            },
            {
                onSuccess: (resp) => {
                    setModalIsOpen(false);
                    const newPlanId = resp.createCustomPlanFromPlan;
                    if (onCreated) {
                        onCreated(newPlanId);
                    } else {
                        // Full page load ensures building/plan queries align everywhere; prefer onCreated when possible.
                        window.location.href = `/buildings/${buildingModelUid}/plans/${newPlanId}/custom/baseline`;
                    }
                },
            }
        );
        setPlanName("");
    };

    const isInvalid = !planName.trim();

    return (
        <Modal
            open={modalIsOpen}
            onCancel={onCancel}
            title="Create a Custom Plan"
            className={styles.modalContainer}
            footer={
                <CreateCustomPlanModalFooter
                    onSave={() => modalSubmitHandler()}
                    onCancel={onCancel}
                    isInvalid={isInvalid}
                    isLoading={isLoading}
                />
            }
        >
            <div>
                <div className={styles.firstHeader}>
                    <p>
                        A custom plan starts out as a copy of Recommendations. Once created, you can edit, add, or
                        delete projects at will. Note that the modeling engine will only use the projects listed. It
                        will not add any of its own recommendations.
                        <br />
                        <br />
                        Get started by giving your new custom plan a name:
                    </p>
                </div>
                <div className="form-input-wrapper">
                    <div>
                        <input
                            style={{
                                width: "100%",
                            }}
                            placeholder="PLAN NAME"
                            value={planName}
                            className="form-input"
                            onChange={handleNameChange}
                            disabled={isLoading}
                        />
                    </div>
                    {errorMessage && (
                        <Paragraph size="small" style={{ color: "var(--audette-content-negative)" }}>
                            {errorMessage}
                        </Paragraph>
                    )}
                </div>
            </div>
        </Modal>
    );
}

interface CreateCustomPlanModalFooterProps {
    onSave: () => void;
    onCancel: () => void;
    isInvalid: boolean;
    isLoading: boolean;
}

function CreateCustomPlanModalFooter({ onSave, onCancel, isInvalid, isLoading }: CreateCustomPlanModalFooterProps) {
    return (
        <div className="bulk-edit-tags__footer" style={{ marginTop: "30px" }}>
            <Button type="primary" onClick={onSave} disabled={isInvalid || isLoading}>
                {isLoading ? "Creating..." : "Create"}
            </Button>
            {!isLoading && (
                <Button type="link" onClick={onCancel} style={{ color: "var(--audette-black)" }} disabled={isLoading}>
                    Cancel
                </Button>
            )}
        </div>
    );
}

export default CreateCustomPlanModal;
